import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import changelogData from "../data/changelog.json";
import { useMetronome } from "../hooks/useMetronome";
import { useLoadingSequence } from "../hooks/useLoadingSequence";
import { useZenMode } from "../hooks/useZenMode";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { useScrollHide } from "../hooks/useScrollHide";
import { useToast } from "../hooks/useToast";
import { isFeatureEnabled } from "../config/featureFlags";
import "../utils/featureFlagUtils"; // Import for development helpers
import { isIOSDevice } from "../utils/deviceDetection";
import { Beats } from "./Beats";
import { ClickAudio } from "./ClickAudio";
import { MainControls } from "./MainControls";
import { ControlButtons } from "./ControlButtons";
import { ToggleControls } from "./ToggleControls";
import type { ToggleControlsProps } from "./ToggleControls";
import type { Bar } from "./SongStructureEditor";
import { StarryBackground } from "./StarryBackground";
import { KeyboardShortcuts } from "./KeyboardShortcuts";
import { TextIconButton } from "./TextIconButton";
import { PromptModal } from "./PromptModal";
import { BarSphere } from "./BarSphere";
import { Timer } from "./Timer";
import { LuMenu } from "react-icons/lu";
import { FaMoon, FaSun, FaInfo, FaKeyboard } from "react-icons/fa";
import { RiResetLeftLine } from "react-icons/ri";
import { ChangelogModal } from "./ChangelogModal";
import { WhatsNewButton } from "./WhatsNewButton";
import { SupportModal } from "./SupportModal";

export function Metronome() {
  const navigate = useNavigate();

  // Local storage helpers
  const getStoredString = (key: string, fallback: string): string => {
    try {
      const value = localStorage.getItem(key);
      return value !== null ? value : fallback;
    } catch {
      return fallback;
    }
  };

  const getStoredNumber = (key: string, fallback: number): number => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return fallback;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  };

  const getStoredBoolean = (key: string, fallback: boolean): boolean => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return fallback;
      return raw === "true";
    } catch {
      return fallback;
    }
  };

  const [isRunning, setIsRunning] = useState(false);
  const [currentBeat, setCurrentBeat] = useState(0);
  const [currentSubBeat, setCurrentSubBeat] = useState(0);
  const [bpm, setBpm] = useState(() => getStoredNumber("bpm", 120));
  const [beatsPerBar, setBeatsPerBar] = useState(() =>
    getStoredNumber("beatsPerBar", 4),
  );
  const [noteValue, setNoteValue] = useState(() =>
    getStoredNumber("noteValue", 4),
  ); // 4 = quarter note, 8 = eighth note, 16 = sixteenth note
  const [subdivision, setSubdivision] = useState(() => {
    // If subdivisions feature is disabled, always use 1 (no subdivision)
    if (!isFeatureEnabled("subdivisions")) {
      return 1;
    }
    const allowed = [1, 3, 5, 6, 7];
    const stored = getStoredNumber("subdivision", 1);
    return allowed.includes(stored) ? stored : 1;
  }); // 1 = no subdivision, 3 = triplets, 5 = quintuplets, etc.
  const [isMuted, setIsMuted] = useState(() =>
    getStoredBoolean("isMuted", false),
  );
  const [volume, setVolume] = useState(() => getStoredNumber("volume", 1.0));
  const [reverbAmount, setReverbAmount] = useState(() =>
    getStoredNumber("reverbAmount", 0.0),
  ); // Default 10% reverb
  const [dialFlashingEnabled, setDialFlashingEnabled] = useState(() =>
    getStoredBoolean("dialFlashingEnabled", true),
  );
  const [stressBeatEnabled, setStressBeatEnabled] = useState(() =>
    getStoredBoolean("stressFirstBeat", true),
  );
  const [stressedBeats, setStressedBeats] = useState(() => {
    try {
      const stored = localStorage.getItem("stressedBeats");
      return stored ? JSON.parse(stored) : [1]; // Default to first beat stressed
    } catch {
      return [1];
    }
  });
  const [screenFlashingEnabled, setScreenFlashingEnabled] = useState(() =>
    getStoredBoolean("screenFlashingEnabled", false),
  );
  const [zenModeEnabled, setZenModeEnabled] = useState(() =>
    getStoredBoolean("zenModeEnabled", false),
  );
  const [beatNumbersEnabled, setBeatNumbersEnabled] = useState(() =>
    getStoredBoolean("beatNumbersEnabled", false),
  );
  const [polyrhythmEnabled, setPolyrhythmEnabled] = useState(() =>
    getStoredBoolean("polyrhythmEnabled", false),
  );
  const [countInEnabled, setCountInEnabled] = useState(() =>
    getStoredBoolean("countInEnabled", false),
  );
  const [isScreenFlashing, setIsScreenFlashing] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [showChangelogModal, setShowChangelogModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const supportReminderPendingRef = useRef(false);
  const isRunningRef = useRef(isRunning);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [isSongStructureEditorOpen, setIsSongStructureEditorOpen] =
    useState(false);
  const [isProgressiveTempoEditorOpen, setIsProgressiveTempoEditorOpen] =
    useState(false);
  const [topButtonsExpanded, setTopButtonsExpanded] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const hasShownIOSWarningRef = useRef(false);

  // Track screen size
  useEffect(() => {
    const updateScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= 1024);
    };

    updateScreenSize();
    window.addEventListener("resize", updateScreenSize);
    return () => window.removeEventListener("resize", updateScreenSize);
  }, []);

  // Auto-collapse menu after 10 seconds on medium/small screens
  useEffect(() => {
    if (topButtonsExpanded && window.innerWidth <= 1024) {
      const timer = setTimeout(() => {
        setTopButtonsExpanded(false);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [topButtonsExpanded]);
  const [clickStyle, setClickStyle] = useState(() =>
    getStoredString("clickStyle", "Quartz"),
  );
  const [audioSettingsExpanded, setAudioSettingsExpanded] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    getStoredBoolean("isDarkMode", true),
  ); // Default to dark mode

  // Timer state
  const [timerEnabled, setTimerEnabled] = useState(() =>
    getStoredBoolean("timerEnabled", false),
  );

  // Subdivisions toggle state
  const [subdivisionsEnabled, setSubdivisionsEnabled] = useState(() =>
    getStoredBoolean("subdivisionsEnabled", false),
  );

  // Progressive Tempo toggle state
  const [progressiveTempoEnabled, setProgressiveTempoEnabled] = useState(() =>
    getStoredBoolean("progressiveTempoEnabled", false),
  );

  // Progressive Tempo Reset BPM toggle state
  const [progressiveTempoResetEnabled, setProgressiveTempoResetEnabled] =
    useState(() => getStoredBoolean("progressiveTempoResetEnabled", false));

  // Progressive Tempo bar counter - tracks bars for BPM increment
  const [progressiveTempoBarCount, setProgressiveTempoBarCount] = useState(0);
  // Track if we just started to skip the first bar event
  const progressiveTempoJustStartedRef = useRef(false);
  // Track if we're stopping to skip bar events during stop
  const isStoppingRef = useRef(false);
  // Track initial BPM when starting with progressive tempo
  const progressiveTempoInitialBPMRef = useRef<number | null>(null);

  // Reset progressive tempo bar counter when feature is disabled
  useEffect(() => {
    if (!progressiveTempoEnabled) {
      setProgressiveTempoBarCount(0);
    }
  }, [progressiveTempoEnabled]);

  // Custom Song Structure state
  const [customSongStructureOn, setCustomSongStructureOn] = useState<boolean>(
    () => getStoredBoolean("customSongStructure.enabled", false),
  );
  const songStructureBars = useMemo<Bar[]>(() => {
    try {
      const raw = localStorage.getItem("customSongStructure.bars");
      if (!raw) return [];
      const parsed = JSON.parse(raw) as Bar[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);
  const [savedBars, setSavedBars] = useState<Bar[]>(songStructureBars);
  const totalBars = useMemo(() => {
    if (!savedBars.length) return 0;
    return savedBars.reduce((max, b) => Math.max(max, b.endBar), 0);
  }, [savedBars]);
  const [currentBarNumber, setCurrentBarNumber] = useState<number>(0);

  // Initialize default bars when custom song structure is enabled for the first time
  useEffect(() => {
    if (customSongStructureOn && savedBars.length === 0) {
      const defaultBars: Bar[] = [
        {
          id: Math.random().toString(36).slice(2, 10),
          startBar: 1,
          endBar: 1,
          bpm: 120,
          timeSig: { numerator: 4, denominator: 4 },
        },
      ];
      setSavedBars(defaultBars);
      try {
        localStorage.setItem(
          "customSongStructure.bars",
          JSON.stringify(defaultBars),
        );
      } catch {}
      // Set current bar to 1 when initializing
      setCurrentBarNumber(1);
    }
  }, [customSongStructureOn, savedBars.length]);

  const setCurrentBarNumberWithLog = useCallback(
    (newValue: number | ((prev: number) => number), reason: string) => {
      setCurrentBarNumber(newValue);
    },
    [],
  );
  const [loopSongStructure, setLoopSongStructure] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem("customSongStructure.loop");
      if (raw == null) return true; // Default to true
      return JSON.parse(raw) === true;
    } catch {
      return true; // Default to true on error
    }
  });

  const barAdvanceEnabledRef = useRef<boolean>(false);
  const startBarRef = useRef<number>(1);
  const isAdvancingBarRef = useRef<boolean>(false);

  const setBarAdvanceEnabled = useCallback((value: boolean, reason: string) => {
    barAdvanceEnabledRef.current = value;
  }, []);

  const setStartBar = useCallback((value: number, reason: string) => {
    startBarRef.current = value;
  }, []);

  const setIsAdvancingBar = useCallback((value: boolean, reason: string) => {
    isAdvancingBarRef.current = value;
  }, []);
  const [isChangingBar, setIsChangingBar] = useState<boolean>(false);
  const isChangingBarRef = useRef<boolean>(false);
  const getBarForNumber = useCallback(
    (barNum: number): Bar | undefined => {
      return savedBars.find((b) => barNum >= b.startBar && barNum <= b.endBar);
    },
    [savedBars],
  );
  const applyBarSettings = useCallback(
    (barNum: number, isInitialSetup: boolean = false) => {
      const bar = getBarForNumber(barNum);
      if (!bar) {
        return;
      }

      // Only set flag if we're not doing initial setup (starting the metronome)
      if (!isInitialSetup) {
        isChangingBarRef.current = true;
        setIsChangingBar(true);
      }

      // Apply settings directly - the issue was in the bar advancement logic, not here
      setBpm(bar.bpm);
      setBeatsPerBar(bar.timeSig.numerator);
      setNoteValue(bar.timeSig.denominator);

      // Reset flag after a delay to allow the audio scheduler to process the changes
      if (!isInitialSetup) {
        setTimeout(() => {
          isChangingBarRef.current = false;
          setIsChangingBar(false);
        }, 50);
      }
    },
    [getBarForNumber],
  );

  // Use custom hooks
  const { beatsVisible, uiVisible, introCompleted, setUiVisible } =
    useLoadingSequence();
  const { handleInteraction } = useZenMode(
    zenModeEnabled,
    introCompleted,
    setUiVisible,
  );
  const isScrollHidden = useScrollHide(50);
  const { showInfo } = useToast();

  // Keep latest running state for delayed effects
  useEffect(() => {
    isRunningRef.current = isRunning;
  }, [isRunning]);

  // Show iOS warning on app load
  useEffect(() => {
    if (isIOSDevice() && !hasShownIOSWarningRef.current) {
      const timer = setTimeout(() => {
        showInfo(
          "Silent Mode should be turned off on your iOS device for the metronome audio to work.",
        );
        hasShownIOSWarningRef.current = true;
      }, 1000); // 1 second delay

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once

  // One-time support reminder: show after delay if not seen
  useEffect(() => {
    let timer: number | undefined;
    try {
      const seen = localStorage.getItem("supportReminder.seen");
      if (seen === "true") {
        return;
      }
    } catch {
      // Ignore storage errors; just don't block the reminder logic
    }

    // 10 minutes
    timer = window.setTimeout(
      () => {
        if (isRunningRef.current) {
          // Defer showing until playback stops
          supportReminderPendingRef.current = true;
        } else {
          setShowSupportModal(true);
        }
      },
      10 * 60 * 1000,
    );

    return () => {
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, []);

  // If support reminder became pending while running, show it on stop
  useEffect(() => {
    if (!isRunning && supportReminderPendingRef.current) {
      supportReminderPendingRef.current = false;
      setShowSupportModal(true);
    }
  }, [isRunning]);

  // Auto-show "What's New" modal once per released version
  useEffect(() => {
    const latestEntry = (changelogData as any[])[0];
    if (!latestEntry || !latestEntry.version) return;
    const latestVersion = String(latestEntry.version);
    try {
      const seenVersion = localStorage.getItem("changelog.lastSeenVersion");
      if (seenVersion !== latestVersion) {
        setShowChangelogModal(true);
      }
    } catch {
      // Ignore storage errors (e.g., privacy mode)
    }
  }, []);

  // One-time migration: ensure progressive tempo defaults are 1/1 if previously 5/4 or missing
  useEffect(() => {
    try {
      const version = localStorage.getItem("progressiveTempo.version");
      if (version !== "2") {
        const savedInc = localStorage.getItem("progressiveTempo.increment");
        const savedFreq = localStorage.getItem("progressiveTempo.frequency");
        const shouldSetDefaults =
          (!savedInc && !savedFreq) || (savedInc === "5" && savedFreq === "4");
        if (shouldSetDefaults) {
          localStorage.setItem("progressiveTempo.increment", "1");
          localStorage.setItem("progressiveTempo.frequency", "1");
        }
        localStorage.setItem("progressiveTempo.version", "2");
      }
    } catch {}
  }, []);

  // Handle beat dot click to toggle stressed beat
  const handleBeatClick = useCallback(
    (beatNumber: number) => {
      if (stressBeatEnabled) {
        setStressedBeats((prev: number[]) => {
          if (prev.includes(beatNumber)) {
            // Remove beat from stressed array
            return prev.filter((beat: number) => beat !== beatNumber);
          } else {
            // Add beat to stressed array
            return [...prev, beatNumber].sort((a: number, b: number) => a - b);
          }
        });
      }
    },
    [stressBeatEnabled],
  );

  // Reset stressed beats to only first beat when custom song structure is enabled
  useEffect(() => {
    if (customSongStructureOn && stressBeatEnabled) {
      // Only keep the first beat stressed, remove all others
      setStressedBeats((prev: number[]) => {
        // If first beat is not stressed or there are other stressed beats, reset to only first beat
        if (!prev.includes(1) || prev.length > 1) {
          return [1];
        }
        return prev; // Already correct, no change needed
      });
    }
  }, [customSongStructureOn, stressBeatEnabled]);

  // Track Custom Song Structure modal open/close to disable shortcuts
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<boolean>).detail;
      setIsSongStructureEditorOpen(Boolean(detail));
    };
    window.addEventListener(
      "customSongStructure:open",
      handler as EventListener,
    );
    return () =>
      window.removeEventListener(
        "customSongStructure:open",
        handler as EventListener,
      );
  }, []);

  // Track Progressive Tempo Editor modal open/close to disable shortcuts
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<boolean>).detail;
      setIsProgressiveTempoEditorOpen(Boolean(detail));
    };
    window.addEventListener("progressiveTempo:open", handler as EventListener);
    return () =>
      window.removeEventListener(
        "progressiveTempo:open",
        handler as EventListener,
      );
  }, []);

  // Theme effect - apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      isDarkMode ? "dark" : "light",
    );
  }, [isDarkMode]);

  // Persist user settings
  useEffect(() => {
    try {
      localStorage.setItem("bpm", String(bpm));
      localStorage.setItem("beatsPerBar", String(beatsPerBar));
      localStorage.setItem("noteValue", String(noteValue));
      localStorage.setItem("subdivision", String(subdivision));
      localStorage.setItem("volume", String(volume));
      localStorage.setItem("isMuted", String(isMuted));
      localStorage.setItem("reverbAmount", String(reverbAmount));
      localStorage.setItem("dialFlashingEnabled", String(dialFlashingEnabled));
      localStorage.setItem("stressFirstBeat", String(stressBeatEnabled));
      localStorage.setItem("stressedBeats", JSON.stringify(stressedBeats));
      localStorage.setItem(
        "screenFlashingEnabled",
        String(screenFlashingEnabled),
      );
      localStorage.setItem("isDarkMode", String(isDarkMode));
      localStorage.setItem("clickStyle", String(clickStyle));
      localStorage.setItem("zenModeEnabled", String(zenModeEnabled));
      localStorage.setItem("beatNumbersEnabled", String(beatNumbersEnabled));
      localStorage.setItem("polyrhythmEnabled", String(polyrhythmEnabled));
      localStorage.setItem("countInEnabled", String(countInEnabled));
      localStorage.setItem(
        "customSongStructure.loop",
        String(loopSongStructure),
      );
      localStorage.setItem("subdivisionsEnabled", String(subdivisionsEnabled));
      localStorage.setItem(
        "progressiveTempoEnabled",
        String(progressiveTempoEnabled),
      );
      localStorage.setItem(
        "progressiveTempoResetEnabled",
        String(progressiveTempoResetEnabled),
      );
      localStorage.setItem("timerEnabled", String(timerEnabled));
    } catch {
      // Ignore storage errors (e.g., privacy mode)
    }
  }, [
    bpm,
    beatsPerBar,
    noteValue,
    subdivision,
    volume,
    isMuted,
    reverbAmount,
    dialFlashingEnabled,
    stressBeatEnabled,
    stressedBeats,
    screenFlashingEnabled,
    isDarkMode,
    clickStyle,
    zenModeEnabled,
    beatNumbersEnabled,
    polyrhythmEnabled,
    countInEnabled,
    loopSongStructure,
    subdivisionsEnabled,
    progressiveTempoEnabled,
    progressiveTempoResetEnabled,
    timerEnabled,
  ]);

  const handleBeat = useCallback(
    (beat: number) => {
      if (isRunning) {
        setCurrentBeat(beat);
        window.dispatchEvent(new CustomEvent("beat", { detail: beat }));
      }
    },
    [isRunning],
  );

  const handleSubBeat = useCallback(
    (subBeat: number) => {
      if (isRunning) {
        setCurrentSubBeat(subBeat);
      }
    },
    [isRunning],
  );

  // Handle any interaction to exit zen mode
  const handleInteractionWrapper = useCallback(
    (event: Event) => {
      if (handleInteraction(event)) {
        setZenModeEnabled(false);
      }
    },
    [handleInteraction],
  );

  // Add event listeners for keyboard and mouse interactions
  useEffect(() => {
    const handleClick = (event: MouseEvent) => handleInteractionWrapper(event);
    const handleTouchStart = (event: TouchEvent) =>
      handleInteractionWrapper(event);

    if (zenModeEnabled) {
      document.addEventListener("click", handleClick);
      document.addEventListener("touchstart", handleTouchStart);
    }

    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("touchstart", handleTouchStart);
    };
  }, [zenModeEnabled, handleInteractionWrapper]);

  // Screen flash effect on each beat (only if enabled)
  useEffect(() => {
    if (currentBeat > 0 && screenFlashingEnabled && isRunning) {
      setIsScreenFlashing(true);
      const timer = setTimeout(() => setIsScreenFlashing(false), 75);
      return () => clearTimeout(timer);
    } else {
      setIsScreenFlashing(false);
    }
  }, [currentBeat, screenFlashingEnabled, isRunning]);

  const { reset } = useMetronome(
    bpm,
    isRunning,
    beatsPerBar,
    noteValue,
    subdivision,
    handleBeat,
    handleSubBeat,
    true, // Disable the old metronome hook since we're using audio scheduling
  );

  // When subdivisions are turned off, reset to none
  useEffect(() => {
    if (!subdivisionsEnabled && subdivision !== 1) {
      setSubdivision(1);
      setCurrentBeat(0);
      setCurrentSubBeat(0);
      reset();
      isStoppingRef.current = true;
      window.dispatchEvent(new CustomEvent("bar"));
    }
  }, [subdivisionsEnabled, subdivision, reset]);

  // When audio scheduling is active, drive UI from precise audio ticks
  useEffect(() => {
    const onAudioTick = (e: Event) => {
      const detail = e as CustomEvent<{ beat: number; subBeat: number }>;
      const { beat, subBeat } = detail.detail || { beat: 0, subBeat: 0 };
      if (!isRunning) return;
      setCurrentSubBeat(subBeat);
      if (subBeat === 1) {
        setCurrentBeat(beat);
      }
    };
    window.addEventListener("audio-tick", onAudioTick as EventListener);
    return () =>
      window.removeEventListener("audio-tick", onAudioTick as EventListener);
  }, [isRunning]);

  // Ensure metronome stops when the song structure editor opens
  useEffect(() => {
    if (isSongStructureEditorOpen && isRunning) {
      setIsRunning(false);
      reset();
      setCurrentBeat(0);
      setCurrentSubBeat(0);
      barAdvanceEnabledRef.current = false;
      isAdvancingBarRef.current = false;
      setIsChangingBar(false);
      isStoppingRef.current = true;
      window.dispatchEvent(new CustomEvent("bar"));
    }
  }, [isSongStructureEditorOpen, isRunning, reset]);

  // Respond to bar events to advance the song structure
  useEffect(() => {
    const onBar = () => {
      if (!isRunning) {
        return;
      }
      if (!customSongStructureOn || savedBars.length === 0) {
        return;
      }
      // Prevent multiple simultaneous bar advances
      if (isAdvancingBarRef.current) {
        return;
      }

      if (!barAdvanceEnabledRef.current) {
        // Arm advancement after the first bar event post-start
        // But only if we're not currently advancing (i.e., this is the first bar after start)
        setBarAdvanceEnabled(true, "arming bar advancement");
        return;
      }

      setIsAdvancingBar(true, "starting bar advancement");

      // Calculate the next bar number
      const nextBarNumber =
        currentBarNumber === 0 ? startBarRef.current : currentBarNumber + 1;

      if (totalBars === 0) {
        setCurrentBarNumberWithLog(nextBarNumber, "no total bars");
        setIsAdvancingBar(false, "bar advancement completed");
        return;
      }

      if (nextBarNumber > totalBars) {
        if (loopSongStructure) {
          setCurrentBarNumberWithLog(1, "looping back to bar 1");
          applyBarSettings(1);
          setIsAdvancingBar(false, "bar advancement completed");
          return;
        }
        // Loop is off: stop the metronome when last bar finishes
        setIsRunning(false);
        setBarAdvanceEnabled(false, "stopping metronome");
        setCurrentBeat(0);
        setCurrentSubBeat(0);
        setCurrentBarNumberWithLog(totalBars, "reached end of song");
        setIsAdvancingBar(false, "bar advancement completed");
        // Don't dispatch bar event here - it can cause unwanted re-arming of advancement
        // The visual reset will happen when isRunning becomes false
        return;
      }

      // Update to next bar and apply its settings
      setCurrentBarNumberWithLog(nextBarNumber, "advancing to next bar");
      applyBarSettings(nextBarNumber);

      // Reset the flag after a short delay to allow state updates to complete
      setTimeout(() => {
        setIsAdvancingBar(false, "bar advancement completed");
      }, 100);
    };
    window.addEventListener("bar" as any, onBar as EventListener);
    return () =>
      window.removeEventListener("bar" as any, onBar as EventListener);
  }, [
    applyBarSettings,
    customSongStructureOn,
    isRunning,
    loopSongStructure,
    savedBars.length,
    totalBars,
    currentBarNumber,
    setBarAdvanceEnabled,
    setCurrentBarNumberWithLog,
    setIsAdvancingBar,
  ]);

  // Progressive Tempo: increment BPM on bar changes
  useEffect(() => {
    const onBar = () => {
      if (!isRunning || !progressiveTempoEnabled || customSongStructureOn) {
        return;
      }

      // Skip bar events when stopping
      if (isStoppingRef.current) {
        isStoppingRef.current = false;
        return;
      }

      // Skip the first bar event after starting
      if (progressiveTempoJustStartedRef.current) {
        progressiveTempoJustStartedRef.current = false;
        return;
      }

      // Load progressive tempo settings from localStorage
      const incrementStr = localStorage.getItem("progressiveTempo.increment");
      const frequencyStr = localStorage.getItem("progressiveTempo.frequency");

      const increment = incrementStr ? parseInt(incrementStr, 10) : 1;
      const frequency = frequencyStr ? parseInt(frequencyStr, 10) : 1;

      // Validate settings
      if (
        !Number.isFinite(increment) ||
        !Number.isFinite(frequency) ||
        increment <= 0 ||
        frequency <= 0
      ) {
        return;
      }

      // Increment bar counter
      setProgressiveTempoBarCount((prev) => {
        const newCount = prev + 1;

        // Check if we should increment BPM
        if (newCount >= frequency) {
          const newBPM = Math.min(bpm + increment, 300); // Cap at 300 BPM
          if (newBPM !== bpm) {
            setBpm(newBPM);
          }
          return 0; // Reset counter
        }

        return newCount;
      });
    };

    window.addEventListener("bar" as any, onBar as EventListener);
    return () =>
      window.removeEventListener("bar" as any, onBar as EventListener);
  }, [
    isRunning,
    progressiveTempoEnabled,
    customSongStructureOn,
    bpm,
    progressiveTempoBarCount,
  ]);

  const handleToggle = () => {
    setIsRunning((prev) => {
      const next = !prev;
      if (!next) {
        reset();
        setCurrentBeat(0);
        setCurrentSubBeat(0);
        // Reset bar tracking BEFORE dispatching bar event to avoid accidental advance
        setBarAdvanceEnabled(false, "song structure editor opened");
        setIsAdvancingBar(false, "song structure editor opened");
        isChangingBarRef.current = false;
        setIsChangingBar(false);
        // Reset progressive tempo bar counter when stopping
        setProgressiveTempoBarCount(0);
        progressiveTempoJustStartedRef.current = false;
        isStoppingRef.current = true;
        // Reset BPM to initial value if progressive tempo reset is enabled
        if (
          progressiveTempoEnabled &&
          progressiveTempoResetEnabled &&
          progressiveTempoInitialBPMRef.current !== null
        ) {
          setBpm(progressiveTempoInitialBPMRef.current);
          progressiveTempoInitialBPMRef.current = null;
        }
        // If stopping during count-in, restore the intended start bar for display
        if (
          customSongStructureOn &&
          savedBars.length > 0 &&
          countInEnabled &&
          currentBarNumber === 0
        ) {
          setCurrentBarNumberWithLog(
            startBarRef.current || 1,
            "stopping during count-in",
          );
        }
        // Preserve currentBarNumber when stopping so we can resume from this bar
        window.dispatchEvent(new CustomEvent("bar")); // Reset visuals
      } else {
        // Starting playback: initialize bar tracking if song structure is on
        // Start from the beginning of the CURRENT bar (or bar 1 if unset)
        reset();
        setCurrentBeat(0);
        setCurrentSubBeat(0);
        // Reset progressive tempo bar counter when starting
        setProgressiveTempoBarCount(0);
        progressiveTempoJustStartedRef.current = true;
        // Save initial BPM if progressive tempo is enabled
        if (progressiveTempoEnabled && !customSongStructureOn) {
          progressiveTempoInitialBPMRef.current = bpm;
        }
        if (customSongStructureOn && savedBars.length > 0) {
          const targetStartBar =
            currentBarNumber && currentBarNumber > 0 ? currentBarNumber : 1;
          setStartBar(targetStartBar, "setting start bar for playback");
          if (countInEnabled) {
            // Pre-roll: play one bar using CURRENT bar settings without advancing yet
            setCurrentBarNumberWithLog(0, "starting with count-in enabled");
            applyBarSettings(targetStartBar, true);
          } else {
            setCurrentBarNumberWithLog(
              targetStartBar,
              "starting without count-in",
            );
            applyBarSettings(targetStartBar, true);
          }
          // Do not advance immediately on the first bar event after start
          setBarAdvanceEnabled(false, "stopping metronome");
        } else {
          setBarAdvanceEnabled(false, "stopping metronome");
          setIsAdvancingBar(false, "bar advancement completed");
          isChangingBarRef.current = false;
          setIsChangingBar(false);
          setCurrentBarNumberWithLog(
            0,
            "starting without custom song structure",
          );
        }
      }
      return next;
    });
  };

  const handleReset = () => {
    reset();
    setCurrentBeat(0);
    setCurrentSubBeat(0);
    // Reset progressive tempo bar counter
    setProgressiveTempoBarCount(0);
    progressiveTempoJustStartedRef.current = false;
    isStoppingRef.current = true;
    window.dispatchEvent(new CustomEvent("bar")); // Reset visuals
    // Reset bar tracking
    if (customSongStructureOn && savedBars.length > 0) {
      setCurrentBarNumberWithLog(1, "handleReset with custom song structure");
      applyBarSettings(1, true);
      barAdvanceEnabledRef.current = true;
    } else {
      setCurrentBarNumberWithLog(
        0,
        "handleReset without custom song structure",
      );
      barAdvanceEnabledRef.current = false;
    }
  };

  const handleBPMChange = (newBPM: number) => {
    setBpm(newBPM);
  };

  const handleBeatsPerBarChange = (newBeatsPerBar: number) => {
    setBeatsPerBar(newBeatsPerBar);
    // Reset current beat when changing time signature
    setCurrentBeat(0);
    setCurrentSubBeat(0);
    reset();
    isStoppingRef.current = true;
    window.dispatchEvent(new CustomEvent("bar"));
  };

  const handleNoteValueChange = (newNoteValue: number) => {
    setNoteValue(newNoteValue);
    // Reset current beat when changing note value
    setCurrentBeat(0);
    setCurrentSubBeat(0);
    reset();
    isStoppingRef.current = true;
    window.dispatchEvent(new CustomEvent("bar"));
  };

  const handleSubdivisionChange = (newSubdivision: number) => {
    // Only allow subdivision changes if the feature is enabled
    if (isFeatureEnabled("subdivisions")) {
      setSubdivision(newSubdivision);
      // Reset current beat when changing subdivision
      setCurrentBeat(0);
      setCurrentSubBeat(0);
      reset();
      isStoppingRef.current = true;
      window.dispatchEvent(new CustomEvent("bar"));
    }
  };

  const handleMuteToggle = () => {
    setIsMuted((prev) => !prev);
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
  };

  const handleResetToDefault = () => {
    setShowResetConfirmation(true);
  };

  const handleCancelReset = () => {
    setShowResetConfirmation(false);
  };

  const handleConfirmReset = () => {
    localStorage.clear();
    window.location.reload();
  };

  // Timer completion handler
  const handleTimerComplete = useCallback(() => {
    if (isRunning) {
      setIsRunning(false);
      reset();
      setCurrentBeat(0);
      setCurrentSubBeat(0);
      // Reset bar tracking
      setBarAdvanceEnabled(false, "timer completed");
      setIsAdvancingBar(false, "timer completed");
      isChangingBarRef.current = false;
      setIsChangingBar(false);
      // Reset progressive tempo bar counter
      setProgressiveTempoBarCount(0);
      progressiveTempoJustStartedRef.current = false;
      isStoppingRef.current = true;
      // Reset BPM to initial value if progressive tempo reset is enabled
      if (
        progressiveTempoEnabled &&
        progressiveTempoResetEnabled &&
        progressiveTempoInitialBPMRef.current !== null
      ) {
        setBpm(progressiveTempoInitialBPMRef.current);
        progressiveTempoInitialBPMRef.current = null;
      }
      window.dispatchEvent(new CustomEvent("bar")); // Reset visuals
    }
  }, [
    isRunning,
    reset,
    setBarAdvanceEnabled,
    setIsAdvancingBar,
    progressiveTempoEnabled,
    progressiveTempoResetEnabled,
  ]);

  const handleNextBar = () => {
    if (!customSongStructureOn || savedBars.length === 0 || totalBars === 0) {
      return;
    }

    // If loop is off and already at the last bar, do nothing
    if (!loopSongStructure && currentBarNumber >= totalBars) {
      return;
    }

    const wasRunning = isRunning;

    // If loop is on and at the last bar, wrap to bar 1
    const nextBar = currentBarNumber >= totalBars ? 1 : currentBarNumber + 1;

    // If metronome is running, stop it first and disable bar advancement BEFORE setting the new bar
    if (wasRunning) {
      setBarAdvanceEnabled(
        false,
        "disabling bar advancement for manual change",
      );
      setIsAdvancingBar(false, "manual bar change");
      setIsRunning(false);
      reset();
      setCurrentBeat(0);
      setCurrentSubBeat(0);
      isStoppingRef.current = true;
      window.dispatchEvent(new CustomEvent("bar")); // Reset visuals

      // Now it's safe to set the new bar number
      setCurrentBarNumberWithLog(nextBar, "handleNextBar");

      // Wait 50ms, then apply settings and restart
      setTimeout(() => {
        applyBarSettings(nextBar, true);
        setBarAdvanceEnabled(false, "restarting after manual bar change");
        setIsRunning(true);
      }, 50);
    } else {
      // Not running, safe to change immediately
      setCurrentBarNumberWithLog(nextBar, "handleNextBar");
      applyBarSettings(nextBar, true);
      setCurrentBeat(0);
      setCurrentSubBeat(0);
      if (customSongStructureOn && savedBars.length > 0) {
        setBarAdvanceEnabled(true, "enabling bar advancement after next bar");
      }
    }
  };

  const handlePreviousBar = () => {
    if (!customSongStructureOn || savedBars.length === 0 || totalBars === 0) {
      return;
    }

    // If loop is off and already at the first bar, do nothing
    if (!loopSongStructure && currentBarNumber <= 1) {
      return;
    }

    const wasRunning = isRunning;

    // If loop is on and at bar 1, wrap to last bar
    const prevBar = currentBarNumber <= 1 ? totalBars : currentBarNumber - 1;

    // If metronome is running, stop it first and disable bar advancement BEFORE setting the new bar
    if (wasRunning) {
      setBarAdvanceEnabled(
        false,
        "disabling bar advancement for manual change",
      );
      setIsAdvancingBar(false, "manual bar change");
      setIsRunning(false);
      reset();
      setCurrentBeat(0);
      setCurrentSubBeat(0);
      isStoppingRef.current = true;
      window.dispatchEvent(new CustomEvent("bar")); // Reset visuals

      // Now it's safe to set the new bar number
      setCurrentBarNumberWithLog(prevBar, "handlePreviousBar");

      // Wait 50ms, then apply settings and restart
      setTimeout(() => {
        applyBarSettings(prevBar, true);
        setBarAdvanceEnabled(false, "restarting after manual bar change");
        setIsRunning(true);
      }, 50);
    } else {
      // Not running, safe to change immediately
      setCurrentBarNumberWithLog(prevBar, "handlePreviousBar");
      applyBarSettings(prevBar, true);
      setCurrentBeat(0);
      setCurrentSubBeat(0);
      if (customSongStructureOn && savedBars.length > 0) {
        setBarAdvanceEnabled(
          true,
          "enabling bar advancement after previous bar",
        );
      }
    }
  };

  // Click style cycling handler
  const handleClickStyleCycle = () => {
    const clickStyles = ["Quartz", "Orb", "Pulsar", "Classic", "Drop"];
    const currentIndex = clickStyles.indexOf(clickStyle);
    const nextIndex = (currentIndex + 1) % clickStyles.length;
    setClickStyle(clickStyles[nextIndex]);
  };

  // Use keyboard shortcuts hook
  useKeyboardShortcuts({
    zenModeEnabled,
    showKeyboardShortcuts,
    setShowKeyboardShortcuts,
    setZenModeEnabled,
    isAnyModalOpen:
      showKeyboardShortcuts ||
      showChangelogModal ||
      isSongStructureEditorOpen ||
      isProgressiveTempoEditorOpen ||
      showResetConfirmation,
    handleToggle,
    handleReset,
    handleMuteToggle,
    handleLoopToggle: () => setLoopSongStructure((prev) => !prev),
    handleBPMChange,
    handleBeatsPerBarChange,
    handleNoteValueChange,
    bpm,
    beatsPerBar,
    noteValue,
    customSongStructureOn,
    setCustomSongStructureOn: (enabled: boolean) =>
      setCustomSongStructureOn(enabled),
    subdivisionsEnabled,
    handleSubdivisionsToggle: () => setSubdivisionsEnabled((prev) => !prev),
    progressiveTempoEnabled,
    handleStressFirstBeatToggle: () => setStressBeatEnabled((prev) => !prev),
    handleDialFlashingToggle: () => setDialFlashingEnabled((prev) => !prev),
    handleScreenFlashingToggle: () => setScreenFlashingEnabled((prev) => !prev),
    handleTimerToggle: () => setTimerEnabled((prev) => !prev),
    handleNextBar,
    handlePreviousBar,
    handleClickStyleCycle,
    handleProgressiveTempoToggle: () =>
      setProgressiveTempoEnabled((prev) => !prev),
  });

  const handleCloseChangelogModal = useCallback(() => {
    const latestEntry = (changelogData as any[])[0];
    const latestVersion = latestEntry?.version
      ? String(latestEntry.version)
      : null;
    if (latestVersion) {
      try {
        localStorage.setItem("changelog.lastSeenVersion", latestVersion);
      } catch {
        // Ignore storage errors
      }
    }
    setShowChangelogModal(false);
  }, []);

  const handleCloseSupportModal = useCallback(() => {
    try {
      localStorage.setItem("supportReminder.seen", "true");
    } catch {
      // Ignore storage errors
    }
    setShowSupportModal(false);
  }, []);

  return (
    <div
      className={`metronome-container ${
        isScreenFlashing ? "screen-flash" : ""
      } ${uiVisible ? "ui-loaded" : ""} ${zenModeEnabled ? "zen-mode" : ""}`}
    >
      {/* Polytrome Logo */}
      <div
        className={`polytrome-logo ${isScrollHidden ? "scroll-hidden" : ""}`}
      >
        <img
          src={isDarkMode ? "/polytrome_logo.svg" : "/polytrome_logo_black.svg"}
          alt="Polytrome"
        />
      </div>

      <StarryBackground isDarkMode={isDarkMode} />

      <ClickAudio
        isRunning={isRunning}
        currentBeat={currentBeat}
        isMuted={isMuted}
        volume={volume}
        stressFirstBeat={stressBeatEnabled}
        stressedBeats={stressedBeats}
        subdivision={subdivision}
        currentSubBeat={currentSubBeat}
        bpm={bpm}
        beatsPerBar={beatsPerBar}
        noteValue={noteValue}
        reverbAmount={reverbAmount}
        onReverbAmountChange={setReverbAmount}
        clickStyle={clickStyle}
        isChangingBar={isChangingBar || isChangingBarRef.current}
      />

      {/* Bar status moved under beats; hidden in zen mode */}

      {/* Timer component - appears above beats when enabled */}
      {isFeatureEnabled("timer") && (
        <div
          className={`timer-container ${beatsVisible ? "beats-visible" : ""} ${
            timerEnabled ? "timer-enabled" : "timer-disabled"
          } ${zenModeEnabled ? "timer-zen-hidden" : ""}`}
        >
          <Timer
            enabled={timerEnabled}
            onTimerComplete={handleTimerComplete}
            isMetronomeRunning={isRunning}
            onMetronomeStop={() => {
              // This will be handled by the timer hook automatically
            }}
          />
        </div>
      )}

      {/* Normal beats component - appears first in normal position */}
      <div
        className={`beats-loading-container ${
          beatsVisible ? "beats-visible" : ""
        } ${zenModeEnabled ? "beats-normal-zen-hidden" : ""}`}
      >
        <Beats
          isRunning={isRunning}
          currentBeat={currentBeat}
          currentSubBeat={currentSubBeat}
          stressFirstBeat={stressBeatEnabled}
          stressedBeats={stressedBeats}
          onBeatClick={handleBeatClick}
          beatsPerBar={beatsPerBar}
          customSongStructureOn={customSongStructureOn}
          beatNumbersEnabled={beatNumbersEnabled}
        />
      </div>

      {/* Bar status under beats when custom song structure is enabled */}
      {/* Moved into ui-container so it fades in/out with the rest of the UI */}

      {/* Zen mode beats component - absolutely positioned in center */}
      <div
        className={`beats-zen-container ${
          zenModeEnabled ? "beats-zen-visible" : ""
        }`}
      >
        <Beats
          isRunning={isRunning}
          currentBeat={currentBeat}
          currentSubBeat={currentSubBeat}
          stressFirstBeat={stressBeatEnabled}
          stressedBeats={stressedBeats}
          onBeatClick={handleBeatClick}
          beatsPerBar={beatsPerBar}
          customSongStructureOn={customSongStructureOn}
          beatNumbersEnabled={beatNumbersEnabled}
        />
      </div>

      {/* Rest of UI - appears after Beats, pushes Beats up */}
      <div className={`ui-container ${uiVisible ? "ui-visible" : ""}`}>
        {/* Bar Sphere - appears when custom song structure is on */}
        <div
          className={`bar-sphere-container ${
            customSongStructureOn && totalBars > 0 ? "visible" : ""
          }`}
        >
          <BarSphere
            currentBar={
              countInEnabled && isRunning && currentBarNumber === 0
                ? 0
                : Math.max(1, currentBarNumber || 1)
            }
            totalBars={totalBars}
            loop={loopSongStructure}
            onStartBarChange={(bar) => {
              // Clamp again defensively
              const target = Math.max(1, Math.min(bar, totalBars || 1));
              // Reset beat counters and jump to requested bar
              setCurrentBeat(0);
              setCurrentSubBeat(0);
              setCurrentBarNumberWithLog(target, "onStartBarChange");
              applyBarSettings(target, true);
              if (customSongStructureOn && savedBars.length > 0) {
                setBarAdvanceEnabled(true, "enabling bar advancement");
              }
            }}
            onNextBar={handleNextBar}
            onPreviousBar={handlePreviousBar}
            onReset={() => {
              reset();
              setCurrentBeat(0);
              setCurrentSubBeat(0);
              setCurrentBarNumberWithLog(1, "onReset");
              applyBarSettings(1, true);
              if (customSongStructureOn && savedBars.length > 0) {
                setBarAdvanceEnabled(true, "enabling bar advancement");
              }
            }}
          />
        </div>
        {/* Main Controls Section */}
        <MainControls
          bpm={bpm}
          beatsPerBar={beatsPerBar}
          noteValue={noteValue}
          subdivision={subdivision}
          volume={volume}
          reverbAmount={reverbAmount}
          clickStyle={clickStyle}
          currentBeat={currentBeat}
          dialFlashingEnabled={dialFlashingEnabled}
          stressFirstBeat={stressBeatEnabled}
          stressedBeats={stressedBeats}
          controlsLocked={customSongStructureOn}
          subdivisionsEnabled={subdivisionsEnabled}
          audioSettingsExpanded={audioSettingsExpanded}
          onBPMChange={handleBPMChange}
          onBeatsPerBarChange={handleBeatsPerBarChange}
          onNoteValueChange={handleNoteValueChange}
          onSubdivisionChange={handleSubdivisionChange}
          onVolumeChange={handleVolumeChange}
          onReverbChange={setReverbAmount}
          onClickStyleChange={setClickStyle}
          onAudioSettingsExpandedChange={setAudioSettingsExpanded}
        />

        {/* Control Buttons */}
        <ControlButtons
          isRunning={isRunning}
          isMuted={isMuted}
          onToggle={handleToggle}
          onMuteToggle={handleMuteToggle}
          onReset={handleReset}
          onBPMChange={handleBPMChange}
          tapDisabled={customSongStructureOn}
          audioSettingsExpanded={audioSettingsExpanded}
        />

        {/* Toggle Controls */}
        {(() => {
          const toggleControlsProps: ToggleControlsProps = {
            dialFlashingEnabled,
            stressFirstBeat: stressBeatEnabled,
            screenFlashingEnabled,
            zenModeEnabled,
            beatNumbersEnabled,
            polyrhythmEnabled,
            onDialFlashingToggle: () =>
              setDialFlashingEnabled(!dialFlashingEnabled),
            onStressFirstBeatToggle: () => {
              const newValue = !stressBeatEnabled;
              setStressBeatEnabled(newValue);
              if (newValue) {
                if (!customSongStructureOn) {
                  showInfo(
                    "Stress Beat enabled - press any beat dot to make it stressed.",
                  );
                }
                // No toast here when custom song structure is on
              }
            },
            onScreenFlashingToggle: () =>
              setScreenFlashingEnabled(!screenFlashingEnabled),
            onZenModeToggle: () => {
              setZenModeEnabled(!zenModeEnabled);
            },
            onBeatNumbersToggle: () =>
              setBeatNumbersEnabled((prev) => !prev),
            onPolyrhythmToggle: () => setPolyrhythmEnabled(!polyrhythmEnabled),
            subdivisionsEnabled,
            onSubdivisionsToggle: () => setSubdivisionsEnabled((prev) => !prev),
            customSongStructureOn,
            onCustomSongStructureToggle: () => {
              const next = !customSongStructureOn;
              setCustomSongStructureOn(next);
              if (next && stressBeatEnabled) {
                showInfo(
                  "Stress Beat in Custom Song Structure mode will only stress the first beat",
                );
              }
            },
            onSongStructureSave: (bars: Bar[]) => {
              setSavedBars(bars);
              try {
                localStorage.setItem(
                  "customSongStructure.bars",
                  JSON.stringify(bars),
                );
              } catch {}
              // If running and feature is on, restart from bar 1 with new settings
              if (isRunning && customSongStructureOn && bars.length > 0) {
                setCurrentBarNumberWithLog(1, "onSongStructureSave");
                applyBarSettings(1, true);
                setBarAdvanceEnabled(true, "enabling bar advancement");
              }
            },
            loopStructure: loopSongStructure,
            onLoopStructureToggle: () => setLoopSongStructure((prev) => !prev),
            countInEnabled,
            onCountInToggle: () => setCountInEnabled((prev) => !prev),
            progressiveTempoEnabled,
            onProgressiveTempoToggle: () =>
              setProgressiveTempoEnabled((prev) => !prev),
            progressiveTempoResetEnabled,
            onProgressiveTempoResetToggle: () =>
              setProgressiveTempoResetEnabled((prev) => !prev),
            timerEnabled,
            onTimerToggle: () => setTimerEnabled((prev) => !prev),
          };
          return <ToggleControls {...toggleControlsProps} />;
        })()}
      </div>

      {/* Top-right buttons container */}
      <div
        className={`top-right-buttons ${isScrollHidden ? "scroll-hidden" : ""}`}
      >
        {/* Toggle button for medium and small screens */}
        <button
          className="top-buttons-toggle"
          onClick={() => setTopButtonsExpanded(!topButtonsExpanded)}
          aria-label="Toggle menu"
        >
          <span
            className={`toggle-icon ${topButtonsExpanded ? "expanded" : ""}`}
          >
            {LuMenu({})}
          </span>
        </button>

        {/* Buttons wrapper for collapse/expand */}
        <div
          className={`top-buttons-wrapper ${
            topButtonsExpanded ? "expanded" : ""
          }`}
        >
          <TextIconButton
            icon={
              isDarkMode
                ? FaMoon({ size: isSmallScreen ? 14 : 20 })
                : FaSun({ size: isSmallScreen ? 14 : 20 })
            }
            text={isDarkMode ? "Light" : "Dark"}
            onClick={() => setIsDarkMode(!isDarkMode)}
            className="color-mode-toggle-btn"
            showText={topButtonsExpanded}
            tooltipText={
              isDarkMode ? "Switch to light mode" : "Switch to dark mode"
            }
            isScrollHidden={false}
          />
          <TextIconButton
            icon={FaInfo({ size: isSmallScreen ? 14 : 20 })}
            text="About"
            onClick={() => navigate("/about")}
            className="info-button"
            showText={topButtonsExpanded}
            tooltipText="About"
            isScrollHidden={false}
          />
          <WhatsNewButton
            isScrollHidden={false}
            topButtonsExpanded={topButtonsExpanded}
            isSmallScreen={isSmallScreen}
            onClick={() => setShowChangelogModal(true)}
          />
          <TextIconButton
            icon={FaKeyboard({ size: isSmallScreen ? 14 : 20 })}
            text="Shortcuts"
            onClick={() => setShowKeyboardShortcuts(true)}
            className="keyboard-shortcuts-btn"
            showText={topButtonsExpanded}
            tooltipText="Keyboard shortcuts"
            isScrollHidden={false}
          />
          <TextIconButton
            icon={RiResetLeftLine({ size: isSmallScreen ? 14 : 20 })}
            text="Reset"
            onClick={handleResetToDefault}
            className="reset-button"
            showText={topButtonsExpanded}
            tooltipText="Reset to default"
            isScrollHidden={false}
          />
          <TextIconButton
            icon={
              <img
                src="/kofiLogo.webp"
                alt="Ko-fi"
                style={{
                  width: isSmallScreen ? "16px" : "20px",
                  height: isSmallScreen ? "16px" : "20px",
                  objectFit: "contain",
                }}
              />
            }
            text="Donate"
            onClick={() =>
              window.open(
                "https://ko-fi.com/polytrome",
                "_blank",
                "noopener,noreferrer",
              )
            }
            className="donation-button"
            showText={topButtonsExpanded}
            tooltipText="Support on Ko-fi"
            isScrollHidden={false}
          />
        </div>
      </div>

      <style>
        {`
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body, html {
            margin: 0;
            padding: 0;
            overflow-x: hidden;
            overflow-y: auto;
            min-height: 100vh;
            width: 100vw;
          }

          .timer-container {
            display: flex;
            justify-content: center;
            align-items: center;
            margin-bottom: 20px;
            opacity: 0;
            transform: translateY(30px);
            transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
            max-height: 100px;
            width: 100%;
            max-width: 200px;
            overflow: visible;
          }

          .timer-container.beats-visible {
            opacity: 1;
            transform: translateY(0);
          }

          .timer-container.timer-zen-hidden {
            opacity: 0;
            transform: translateY(0);
          }

          .timer-container.timer-enabled {
            margin-bottom: 20px;
            width: 100%;
            max-width: 200px;
            max-height: 100px;
            overflow: visible;
          }

          .timer-container.timer-disabled {
            opacity: 0;
            transform: translateY(30px);
            width: 0;
            max-width: 0;
            max-height: 0;
            margin: 0;
            padding: 0;
            pointer-events: none;
            overflow: hidden;
          }

          .timer-container.timer-disabled.beats-visible {
            opacity: 0;
            transform: translateY(30px);
            width: 0;
            max-width: 0;
            max-height: 0;
            margin: 0;
            padding: 0;
            pointer-events: none;
            overflow: hidden;
          }

          @media (max-width: 480px) {
            .timer-container {
              margin-top: 50px;
            }
            
            .timer-container.timer-enabled {
              margin-bottom: 0px;
            }
            
            .timer-container.timer-disabled {
              margin-bottom: 0px;
            }
            
            .beats-loading-container {
              margin-top: 10px;
            }
          }
        `}
      </style>

      {/* Keyboard Shortcuts Overlay */}
      <KeyboardShortcuts
        isOpen={showKeyboardShortcuts}
        onClose={() => setShowKeyboardShortcuts(false)}
      />

      {/* Changelog Modal */}
      <ChangelogModal
        isOpen={showChangelogModal}
        onClose={handleCloseChangelogModal}
      />

      {/* Support Reminder Modal */}
      <SupportModal
        isOpen={showSupportModal}
        onClose={handleCloseSupportModal}
      />

      {/* Reset Confirmation Modal */}
      <PromptModal
        isOpen={showResetConfirmation}
        title="Reset to Default?"
        message="This will reset all settings to their default state. Are you sure you want to proceed?"
        confirmText="Reset"
        cancelText="Cancel"
        onConfirm={handleConfirmReset}
        onCancel={handleCancelReset}
      />
    </div>
  );
}
